import axios from 'axios'
import aqe from '@pushly/aqe'
import { classifyError } from '../error-kind'
import type { Load } from '../load'

/** Flag key to whether the platform has the flag switched on. */
export type FlagCatalog = ReadonlyMap<string, boolean>

type FlagRow = { key?: unknown; isActive?: unknown }

/** A 200 whose body carries no row list. A retry gets the same answer, so it is a deployment problem. */
export class MalformedFlagCatalogError extends Error {
    readonly kind = 'misconfigured'
    constructor() {
        super('The flag table answer carries no list of flags')
        this.name = 'MalformedFlagCatalogError'
    }
}

let catalog: Load<FlagCatalog> = { status: 'idle' }

/** The catalog as a value: idle, loading, ready, or failed. A failed load is never an empty catalog. */
export const getFlagCatalog = (): Load<FlagCatalog> => catalog

/**
 * Fetches the platform flag table and stores key to active. Throws the transport error, or a
 * `misconfigured` error for an answer that is not a list, so the caller can classify it. A failed refresh keeps the last good table.
 */
export async function loadFlagCatalog(): Promise<void> {
    const previous = catalog
    catalog = previous.status === 'ready' || previous.status === 'refreshFailed' ? previous : { status: 'loading' }
    try {
        const response = await axios.get(`${aqe.defaults.publicApiDomain}/flags?pagination=0`)
        const rows: unknown = response.data?.data
        if (!Array.isArray(rows)) throw new MalformedFlagCatalogError()
        const next = new Map<string, boolean>()
        for (const row of rows as FlagRow[]) {
            if (typeof row.key === 'string') next.set(row.key, row.isActive === true)
        }
        catalog = { status: 'ready', data: next }
    } catch (error) {
        const kind = classifyError(error)
        catalog =
            previous.status === 'ready' || previous.status === 'refreshFailed'
                ? { status: 'refreshFailed', data: previous.data, kind }
                : { status: 'failed', kind, error }
        throw error
    }
}

/** Forgets the catalog: for tests and sign-out. */
export function resetFlagCatalog(): void {
    catalog = { status: 'idle' }
}
